import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AGENTIC_FLAGS, AgenticFlagName } from "@/config/featureFlags";
import { AuthContext } from "@/hooks/auth-context";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export interface FeatureFlagState {
  ENABLE_LEADER_INTENT: boolean;
  ENABLE_LEADER_DRIFT_DETECTION: boolean;
  ENABLE_COACH_SIGNALS: boolean;
  ENABLE_COACH_SEQUENCING_ADVICE: boolean;
  ENABLE_COACH_MODE: boolean;
  ENABLE_COACHING_HEALTH_FACTUAL_CONTEXT_V2: boolean;
}

const DEFAULT_FLAGS: FeatureFlagState = {
  ENABLE_LEADER_INTENT: false,
  ENABLE_LEADER_DRIFT_DETECTION: false,
  ENABLE_COACH_SIGNALS: false,
  ENABLE_COACH_SEQUENCING_ADVICE: false,
  ENABLE_COACH_MODE: false,
  ENABLE_COACHING_HEALTH_FACTUAL_CONTEXT_V2: false,
};

const DATABASE_FLAG_KEYS = new Set<keyof FeatureFlagState>([
  AGENTIC_FLAGS.ENABLE_LEADER_INTENT,
  AGENTIC_FLAGS.ENABLE_LEADER_DRIFT_DETECTION,
  AGENTIC_FLAGS.ENABLE_COACH_SIGNALS,
  AGENTIC_FLAGS.ENABLE_COACH_SEQUENCING_ADVICE,
  AGENTIC_FLAGS.ENABLE_COACH_MODE,
  AGENTIC_FLAGS.ENABLE_COACHING_HEALTH_FACTUAL_CONTEXT_V2,
]);

function freshDefaultFlags(): FeatureFlagState {
  return { ...DEFAULT_FLAGS };
}

export function useFeatureFlags() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const isPreviewUser = user?.id === PREVIEW_LEADER_USER_ID;
  const {
    data: flags,
    isLoading,
    isFetching,
    isFetchedAfterMount,
    error,
    refetch,
  } = useQuery({
    queryKey: ["feature-flags", user?.id ?? "anonymous"],
    queryFn: async (): Promise<FeatureFlagState> => {
      if (isPreviewUser) {
        return freshDefaultFlags();
      }
      try {
        const { data, error: queryError } = await supabase
          .from("feature_flags")
          .select("flag_name, enabled");

        if (queryError) {
          console.error("Error fetching feature flags:", queryError);
          return freshDefaultFlags();
        }

        const flagMap = freshDefaultFlags();
        data?.forEach((flag) => {
          if (
            typeof flag.flag_name === "string" &&
            DATABASE_FLAG_KEYS.has(flag.flag_name as keyof FeatureFlagState) &&
            typeof flag.enabled === "boolean"
          ) {
            flagMap[flag.flag_name as keyof FeatureFlagState] = flag.enabled;
          }
        });

        return flagMap;
      } catch (transportError) {
        console.error("Error fetching feature flags:", transportError);
        return freshDefaultFlags();
      }
    },
    staleTime: 0,
    refetchInterval: () =>
      typeof document !== "undefined" && document.visibilityState === "visible"
        ? 60_000
        : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    enabled: !isPreviewUser,
    initialData: isPreviewUser ? freshDefaultFlags() : undefined,
  });

  const resolvedFlags = isPreviewUser
    ? freshDefaultFlags()
    : flags ?? freshDefaultFlags();

  return {
    flags: resolvedFlags,
    isLoading,
    isInitialResolutionPending:
      isLoading || (!isFetchedAfterMount && isFetching),
    error,
    refetch,
    // Convenience accessors
    leaderIntentEnabled: resolvedFlags.ENABLE_LEADER_INTENT,
    driftDetectionEnabled: resolvedFlags.ENABLE_LEADER_DRIFT_DETECTION,
    coachSignalsEnabled: resolvedFlags.ENABLE_COACH_SIGNALS,
    coachSequencingEnabled: resolvedFlags.ENABLE_COACH_SEQUENCING_ADVICE,
    coachModeEnabled: resolvedFlags.ENABLE_COACH_MODE,
    coachingHealthFactualContextV2Enabled:
      resolvedFlags.ENABLE_COACHING_HEALTH_FACTUAL_CONTEXT_V2,
  };
}

// Hook for checking a single flag
export function useFeatureFlag(flagName: AgenticFlagName): boolean {
  const { flags } = useFeatureFlags();
  return flags[flagName] ?? false;
}
